import { UseFormReturn } from 'react-hook-form';
import { compressImage, fileToDataUrl, validateImageFile } from '@/utils/image-compression';

const MAX_UPLOAD_PAYLOAD_MB = 10;
const SITE_SURVEY_PHOTO_UPLOAD_TIMEOUT_MS = 60_000;

type ToastFn = (props: {
  title: string;
  description?: string;
  variant?: 'default' | 'destructive';
}) => void;

interface UploadSiteSurveyPhotoOptions {
  form: UseFormReturn<any>;
  field: string;
  file: File;
  surveyId?: number | string | null;
  installationId?: number | string | null;
  customerId?: number | string | null;
  description: string;
  toast: ToastFn;
  onPhotoUploaded?: () => void;
}

export async function uploadSiteSurveyPhoto({
  form,
  field,
  file,
  surveyId,
  installationId,
  customerId,
  description,
  toast,
  onPhotoUploaded,
}: UploadSiteSurveyPhotoOptions) {
  const validation = validateImageFile(file, 50);
  if (!validation.valid) {
    toast({
      title: 'Invalid File',
      description: validation.error,
      variant: 'destructive',
    });
    return;
  }

  const originalSizeMB = file.size / (1024 * 1024);
  if (originalSizeMB > 5) {
    toast({
      title: 'Compressing Image',
      description: `Processing ${originalSizeMB.toFixed(1)}MB image...`,
    });
  }

  const compressedFile = await compressImage(file, {
    maxWidth: 1920,
    maxHeight: 1080,
    quality: 0.8,
    maxSizeMB: 2,
    outputFormat: 'jpeg',
  });

  const compressedSizeMB = compressedFile.size / (1024 * 1024);
  if (compressedSizeMB > MAX_UPLOAD_PAYLOAD_MB) {
    throw new Error(
      `Image is still too large after compression (${compressedSizeMB.toFixed(1)}MB). Please choose a smaller image.`
    );
  }

  if (!surveyId) {
    const dataUrl = await fileToDataUrl(compressedFile);

    form.setValue(field, dataUrl, {
      shouldValidate: true,
      shouldDirty: true,
      shouldTouch: true,
    });
    await form.trigger(field);
    toast({
      title: 'Image Ready',
      description:
        originalSizeMB > 5
          ? `Compressed ${originalSizeMB.toFixed(1)}MB to ${compressedSizeMB.toFixed(1)}MB. Will upload when survey is saved.`
          : 'Image will be uploaded when you save the survey.',
    });
    onPhotoUploaded?.();
    return;
  }

  const payload = new FormData();
  payload.append('image', compressedFile, compressedFile.name);
  payload.append('filename', compressedFile.name);
  payload.append('siteSurveyId', String(surveyId));
  payload.append('photoType', field);
  payload.append('description', description);
  if (installationId !== undefined && installationId !== null) {
    payload.append('installationId', String(installationId));
  }
  if (customerId !== undefined && customerId !== null) {
    payload.append('customerId', String(customerId));
  }

  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => {
    controller.abort('Site survey photo upload timed out');
  }, SITE_SURVEY_PHOTO_UPLOAD_TIMEOUT_MS);

  let response: Response;
  try {
    response = await fetch('/api/site-surveys/photos/upload-photo', {
      method: 'POST',
      body: payload,
      credentials: 'include',
      signal: controller.signal,
    });
  } catch (error) {
    if (
      controller.signal.aborted ||
      (error instanceof DOMException && error.name === 'AbortError')
    ) {
      throw new Error('Photo upload timed out. Please try again.');
    }
    throw error;
  } finally {
    window.clearTimeout(timeoutId);
  }

  if (!response.ok) {
    let message = '';
    try {
      const errorData = await response.json();
      // The upload route returns { error }, but the global Express error
      // handler returns { message }. Read both so the real reason is shown
      // instead of a generic fallback.
      message = errorData.error || errorData.message || '';
    } catch {
      // Response was not JSON (e.g. an HTML error page from the edge/proxy).
    }
    if (!message) {
      message =
        `Failed to upload photo (HTTP ${response.status} ${response.statusText || ''}).`.trim();
    }
    throw new Error(message);
  }

  const result = await response.json();
  if (!result.success || !result.url) {
    throw new Error('Invalid response from server');
  }

  form.setValue(field, result.url, {
    shouldValidate: true,
    shouldDirty: true,
    shouldTouch: true,
  });
  await form.trigger(field);

  toast({
    title: 'Image Uploaded',
    description:
      originalSizeMB > 5
        ? `Image compressed and uploaded successfully (${originalSizeMB.toFixed(1)}MB to ${compressedSizeMB.toFixed(1)}MB).`
        : 'Image has been successfully uploaded.',
  });

  onPhotoUploaded?.();
}
